import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * 3d-mot — composant jeu principal.
 *
 * Pattern : `<GameShell>` orchestre xstate + bus + polish + boucle staircase.
 * `<PhaseRenderer>` route phase → slot. `<DebriefShell>` fournit le squelette
 * debrief. Adapter Promise-based gere les sub-phases internes (cf. adapter.ts).
 *
 * Composition :
 *   - buildStimulus injecte la valeur staircase dans le payload (via
 *     `buildStimulusPayload` deterministe).
 *   - grader compare picks vs targetIndices (set-equality, all-or-none).
 */
import { useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ActionBarProvider, ActionBarSlot, GameShell, PhaseRenderer, useActionBar, useGameShellFromContext, } from '@labrain/game-shell';
import { Demo } from './demo';
import { buildStimulusPayload, createThreeDMotAdapter, } from './adapter';
import { ThreeDMotDebriefView, ThreeDMotStaircaseView, } from './staircaseView';
import { paradigmMeta, staircaseConfig, variantForLevel } from './params';
function Intro({ onShowDemo, onSkipDemo }) {
    useActionBar({
        primary: (_jsx("button", { type: "button", onClick: onShowDemo, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: "Voir un exemple" })),
        secondary: (_jsx("button", { type: "button", onClick: onSkipDemo, className: "inline-flex h-12 w-full items-center justify-center border border-cyan-border bg-cyan-surface font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-text transition active:scale-[0.985] hover:bg-cyan-bg", children: "Demarrer direct" })),
    });
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s5 py-s6 text-center", children: [_jsx("p", { className: "font-mono text-[9.5px] uppercase tracking-[0.30em] text-cyan-text-faint", children: paradigmMeta.paradigm }), _jsx("h1", { className: "font-display text-[28px] font-medium leading-9 tracking-cy-display text-cyan-text", children: "Suivi 3D" }), _jsx("p", { className: "max-w-[320px] text-[14.5px] leading-5 text-cyan-text-secondary", children: paradigmMeta.goal }), _jsx("p", { className: "max-w-[320px] text-[12.5px] leading-5 text-cyan-text-secondary", children: "Repere les cibles en debut de manche, suis-les pendant qu elles bougent, retrouve-les a l arret." }), _jsx(Link, { to: "/library", className: "text-[12px] text-cyan-text-secondary", children: "\u2190 Retour aux jeux" })] }));
}
function IntroSlot() {
    const shell = useGameShellFromContext();
    return (_jsx(Intro, { onShowDemo: () => shell.startSession(), onSkipDemo: () => shell.skipDemo() }));
}
function DemoSlot() {
    const shell = useGameShellFromContext();
    return (_jsx(Demo, { onComplete: () => shell.markDemoDone(), onSkip: () => shell.markDemoDone() }));
}
function WrapUpSlot() {
    const shell = useGameShellFromContext();
    useMemo(() => {
        try {
            window.localStorage.setItem(`labrain.${paradigmMeta.slug}.lastRun`, JSON.stringify({
                ts: Date.now(),
                threshold: shell.snapshot.staircase.thresholdEstimate,
            }));
        }
        catch {
            /* ignored */
        }
        shell.markWrapUpDone();
        return null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return null;
}
export function ThreeDMotGame({ level = 1 } = {}) {
    const navigate = useNavigate();
    const adapterRef = useRef(null);
    if (adapterRef.current === null) {
        adapterRef.current = createThreeDMotAdapter(level);
    }
    const adapter = adapterRef.current;
    const config = useMemo(() => ({
        gameSlug: paradigmMeta.slug,
        staircaseConfig,
        showDemo: true,
        showCalibrationWarmup: false,
        enableHaptics: true,
        enableSound: true,
        enableVisualFeedback: true,
    }), []);
    return (_jsxs(ActionBarProvider, { children: [_jsx(GameShell, { config: config, adapter: adapter, buildStimulus: (engStim) => {
                    const variant = variantForLevel(level);
                    return buildStimulusPayload(engStim.trialIdx, engStim.value, variant);
                }, grader: (stim, resp) => ({
                    correct: resp.allCorrect,
                    reactionTimeMs: resp.reactionTimeMs,
                    rawInput: { picked: resp.pickedIndices, targets: stim.targetIndices },
                }), children: _jsx(PhaseRenderer, { intro: _jsx(IntroSlot, {}), demo: _jsx(DemoSlot, {}), staircase: _jsx(ThreeDMotStaircaseView, { adapter: adapter, onAbort: () => navigate('/library') }), wrapUp: _jsx(WrapUpSlot, {}), debrief: _jsx(ThreeDMotDebriefView, {}), aborted: null }) }), _jsx(ActionBarSlot, {})] }));
}
export default ThreeDMotGame;
//# sourceMappingURL=game.js.map